import { DescriptionRow, TableOptions } from '@/types/table';
import { ColumnSort } from '@/types/cube';
import { SortingState } from '@tanstack/react-table';
import { useMemo } from 'react';

type RowValueAccessor = {
  getValue: (columnId: string) => unknown;
};

type RowWithOriginal<TData> = {
  original: TData;
};

type RowWithSubRows = {
  subRows?: unknown[];
};

type RowWithLeafRows<TData> = {
  getLeafRows: () => Array<RowWithOriginal<TData>>;
};

export const PaginationOptions: readonly [5, 10, 25, 50, 100] = [5, 10, 25, 50, 100];

export const PAGINATION_OPTIONS: Array<{
  label: `${(typeof PaginationOptions)[number]}`;
  value: (typeof PaginationOptions)[number];
}> = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];

export type PreGroupedRow<T> = { subRows: T[] } & Partial<Record<ConformToNumber<T>, number>>;

/**
 * @summary Sorting function for the table
 * @param rowA
 * @param rowB
 * @param columnId
 * @param comparator
 */
export const sortingFn = <TRow extends RowValueAccessor>(
  rowA: TRow,
  rowB: TRow,
  columnId: string,
  comparator?: (a: TRow, b: TRow) => number,
): number => {
  if (comparator) return comparator(rowA, rowB);
  const valueA = rowA.getValue(columnId);
  const valueB = rowB.getValue(columnId);
  if (typeof valueA === 'number' && typeof valueB === 'number') return valueA - valueB;
  if (valueA === 'N/A' || valueA === null) return -1;
  if (valueB === 'N/A' || valueB === null) return 1;
  return 0;
};

/** Maximum number of columns that can be sorted simultaneously across CES tables. */
export const MAX_SORT_COLUMNS = 3;

/**
 * Caps a sorting state to {@link MAX_SORT_COLUMNS} columns, keeping the highest-priority
 * entries (front of the array) and dropping the oldest ones.
 */
export const capSortColumns = (sorting: SortingState): SortingState =>
  sorting.length <= MAX_SORT_COLUMNS ? sorting : sorting.slice(0, MAX_SORT_COLUMNS);

export const toColumnSorts = (sortingState: SortingState): ColumnSort[] =>
  sortingState.map(sortColumn => ({ field: sortColumn.id, desc: sortColumn.desc }));

export const buildCubeColumnOrder = (
  sortColumn: ColumnSort,
  fieldMapping: Record<string, string | readonly string[]>,
): Array<[string, 'asc' | 'desc']> => {
  const mappedFields = fieldMapping[sortColumn.field];
  if (!mappedFields) return [];

  const sortDirection: 'asc' | 'desc' = sortColumn.desc ? 'desc' : 'asc';
  const cubeFields = typeof mappedFields === 'string' ? [mappedFields] : mappedFields;

  return cubeFields.map((cubeField): [string, 'asc' | 'desc'] => [cubeField, sortDirection]);
};

export const dedupeCubeOrderByField = (order: Array<[string, 'asc' | 'desc']>): Array<[string, 'asc' | 'desc']> => {
  const directionByCubeField = new Map<string, 'asc' | 'desc'>();

  for (const [cubeField, sortDirection] of order) {
    if (!directionByCubeField.has(cubeField)) {
      directionByCubeField.set(cubeField, sortDirection);
    }
  }

  return [...directionByCubeField];
};

export const buildCubeMultiOrder = (
  sorts: ColumnSort[],
  fieldMapping: Record<string, string | readonly string[]>,
): Array<[string, 'asc' | 'desc']> =>
  dedupeCubeOrderByField(sorts.flatMap(sortColumn => buildCubeColumnOrder(sortColumn, fieldMapping)));

export const buildCubeMultiOrderOrDefault = (
  sorts: ColumnSort[],
  fieldMapping: Record<string, string | readonly string[]>,
  defaultOrder: Record<string, 'asc' | 'desc'>,
): Array<[string, 'asc' | 'desc']> | Record<string, 'asc' | 'desc'> => {
  const order = buildCubeMultiOrder(sorts, fieldMapping);
  return order.length ? order : defaultOrder;
};

export const safeStringValue = (value: unknown, defaultPlaceholder: string = '-'): string => {
  if (value === null || value === undefined) return defaultPlaceholder;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return String(value);
  return String(value);
};

export const parseOptionalSortOrder = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'boolean') return undefined;

  const parsedValue = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

/** Placeholder values shown for missing/empty names; treat as empty for sorting (sent to end). */
const NAME_PLACEHOLDERS = new Set(['', '-', '--', 'N/A']);

const isNameEmpty = (value: string | null | undefined): boolean => {
  if (value == null || typeof value !== 'string') return true;
  const trimmed = value.trim();
  return NAME_PLACEHOLDERS.has(trimmed);
};

export function compareStringsWithEmptyLast(
  valueA: string | null | undefined,
  valueB: string | null | undefined,
  emptyPredicate: (value: string | null | undefined) => boolean = value => !value,
): number {
  const aEmpty = emptyPredicate(valueA);
  const bEmpty = emptyPredicate(valueB);

  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  return (valueA ?? '').localeCompare(valueB ?? '', undefined, { numeric: true });
}

/**
 * Shared comparator: sorts ascending with "empty" values last.
 * @param emptyPredicate - returns true for values that should be sorted to the end (default: falsy)
 */
function sortWithNullsLast<TData>(
  rowA: RowWithOriginal<TData>,
  rowB: RowWithOriginal<TData>,
  getValue: (row: RowWithOriginal<TData>) => string | null | undefined,
  emptyPredicate: (value: string | null | undefined) => boolean = value => !value,
): number {
  return compareStringsWithEmptyLast(getValue(rowA), getValue(rowB), emptyPredicate);
}

export const sortByLastNameWithNullsLast = <TData extends { lastName?: string }>(
  rowA: RowWithOriginal<TData>,
  rowB: RowWithOriginal<TData>,
): number => sortWithNullsLast(rowA, rowB, row => row.original.lastName);

export const sortByFirstNameWithNullsLast = <TData extends { firstName?: string }>(
  rowA: RowWithOriginal<TData>,
  rowB: RowWithOriginal<TData>,
): number => sortWithNullsLast(rowA, rowB, row => row.original.firstName);

export const sortByNameWithNullsLast = <TData extends { name?: string }>(
  rowA: RowWithOriginal<TData>,
  rowB: RowWithOriginal<TData>,
): number => sortWithNullsLast(rowA, rowB, row => row.original.name, isNameEmpty);

export const sortByOptionalOrderThenText = <TData>(
  rowA: RowWithOriginal<TData>,
  rowB: RowWithOriginal<TData>,
  {
    getSortOrder,
    getPrimaryText,
    getSecondaryText,
    emptyPredicate = isNameEmpty,
  }: {
    getSortOrder: (row: TData) => number | undefined;
    getPrimaryText: (row: TData) => string | null | undefined;
    getSecondaryText?: (row: TData) => string | null | undefined;
    emptyPredicate?: (value: string | null | undefined) => boolean;
  },
): number => {
  const sortOrderA = getSortOrder(rowA.original);
  const sortOrderB = getSortOrder(rowB.original);

  if (sortOrderA !== undefined && sortOrderB !== undefined && sortOrderA !== sortOrderB) {
    return sortOrderA - sortOrderB;
  }

  const primaryComparison = compareStringsWithEmptyLast(
    getPrimaryText(rowA.original),
    getPrimaryText(rowB.original),
    emptyPredicate,
  );

  if (primaryComparison !== 0) {
    return primaryComparison;
  }

  if (!getSecondaryText) {
    return 0;
  }

  return compareStringsWithEmptyLast(getSecondaryText(rowA.original), getSecondaryText(rowB.original), emptyPredicate);
};

/**
 * Shared date sort for TanStack Table columns.
 * Sorts ascending by date with null/empty values sorted before valid dates.
 *
 * @param rowA - First row to compare
 * @param rowB - Second row to compare
 * @param getDateValue - Accessor to pull the date string from row.original
 */
export const sortByDate = <TData>(
  rowA: RowWithOriginal<TData>,
  rowB: RowWithOriginal<TData>,
  getDateValue: (original: TData) => string | null | undefined,
): number => {
  const rawA = getDateValue(rowA.original);
  const rawB = getDateValue(rowB.original);
  const dateA = rawA ? new Date(rawA) : null;
  const dateB = rawB ? new Date(rawB) : null;

  if (!dateA && !dateB) return 0;
  if (!dateA) return -1;
  if (!dateB) return 1;

  return dateA.getTime() - dateB.getTime();
};

export function getFlattenedData<T>(preGrouped: PreGroupedRow<T>[]): T[] {
  return preGrouped.flatMap(parentRow => parentRow.subRows);
}

export function useFlattenedData<T>(preGrouped: PreGroupedRow<T>[]): T[] {
  return getFlattenedData(preGrouped);
}

export function shouldFlattenGroup<TRow extends RowWithSubRows>(row: TRow): boolean {
  return row.subRows && row.subRows.length === 1;
}

/**
 * Generates table options with commonly used attributes across the project.
 *
 * This function creates a configuration object for table settings, including grouping,
 * pagination, and additional header rows. It is designed to be flexible and reusable
 * throughout the project by accepting optional parameters for customization.
 *
 * @template T - The type of data that will be used in the table.
 *
 * @param {Object} params - The parameters for configuring table options.
 * @param {Array<keyof T>} [params.groupingKeys] - Optional array of keys used for grouping rows in the table.
 * @param {DescriptionRow} [params.additionalHeaders] - Optional additional headers to be displayed in the table.
 */
export function useTableOptions<T>({
  groupingKeys,
  additionalHeaders,
  pageSize,
  defaultSorting,
  displayRowExpand = true,
  displayPageInfoAtBottom = true,
  displayPaginationSelectBox = true,
  hideTableHeaders = false,
  tooltipPlace,
}: {
  groupingKeys?: Array<keyof T>;
  additionalHeaders?: DescriptionRow;
  pageSize?: (typeof PaginationOptions)[number];
  defaultSorting?: SortingState;
  displayRowExpand?: boolean;
  displayPageInfoAtBottom?: boolean;
  displayPaginationSelectBox?: boolean;
  hideTableHeaders?: boolean;
  tooltipPlace?: TableOptions<T>['tooltipPlace'];
} = {}): TableOptions<T> {
  const stringGrouping = groupingKeys?.map(groupId => groupId.toString());
  const groupingKey = stringGrouping?.join(',');

  return useMemo(
    () => ({
      grouping: groupingKeys?.length && stringGrouping,
      pageSize: pageSize || 10,
      defaultSorting,
      displayPageInfoAtBottom,
      hideTableHeaders,
      paginationSelectBox: displayPaginationSelectBox && PAGINATION_OPTIONS,
      shouldFlattenGroup: groupingKeys?.length && shouldFlattenGroup,
      shouldDisplayRowExpand: () => displayRowExpand,
      groupingFlow: 'GroupPrefetched',
      additionalHeaderRows: additionalHeaders && [
        { data: additionalHeaders, styles: { backgroundColor: 'var(--yellow-100)', fontWeight: 'bold' } },
      ],
      tooltipPlace,
    }),
    // stringGrouping is derived from groupingKey; additionalHeaders is expected to be stable per caller
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      groupingKey,
      pageSize,
      defaultSorting,
      displayPageInfoAtBottom,
      hideTableHeaders,
      displayPaginationSelectBox,
      displayRowExpand,
      additionalHeaders,
      tooltipPlace,
    ],
  );
}

export function getAggregateCellTotal<T>({ row, key }: { row: RowWithLeafRows<T>; key: keyof T }): number {
  return row.getLeafRows().reduce((acc, subRow) => {
    if (typeof subRow.original[key] !== 'number') return acc;
    return acc + subRow.original[key];
  }, 0);
}

export type ConformToNumber<T> = {
  [K in keyof T]: NonNullable<T[K]> extends number ? K : never;
}[keyof T];

type GroupAccumulator<T> = {
  subRows: T[];
  aggregates: Partial<Record<ConformToNumber<T>, number>>;
};

export function useGroupedData<T>({
  data,
  groupingKey,
  aggregatedKeys,
}: {
  data: Iterable<T>;
  groupingKey: keyof T;
  aggregatedKeys: Array<ConformToNumber<T>>;
}): PreGroupedRow<T>[] {
  const resultMap = new Map<string, GroupAccumulator<T>>();

  for (const row of data) {
    const key = String(row[groupingKey]);

    if (!resultMap.has(key)) {
      resultMap.set(key, { subRows: [], aggregates: {} });
    }

    const group = resultMap.get(key);

    if (group) {
      group.subRows.push(row);

      aggregatedKeys.forEach(aggKey => {
        const value = row[aggKey];

        if (typeof value === 'number') {
          const currentValue = group.aggregates[aggKey] ?? 0;
          group.aggregates[aggKey] = currentValue + value;
        }
      });
    }
  }

  return Array.from(resultMap.values()).map(group => ({
    subRows: group.subRows,
    ...group.aggregates,
  }));
}
